import React, { useContext } from 'react';
import { FlexWrapper, Name, Section, StyledIcon as HeaderIcon, StyledIcon, WrapperForSections } from './shared';
import {
  RequiredLabel,
  StatefulInput,
  StudioPrintSettingsContext,
  UserContext,
  STUDIO_DISCLOSURE_PAGE_DEFAULT_DISCLOSURE,
} from 'venn-components';
import { AlertNote, Button, Caption, getAppTitle, Label, Link as LinkStyle, Tooltip } from 'venn-ui-kit';
import {
  STUDIO_COVER_PAGE_DEFAULT_DISCLOSURE,
  STUDIO_COVER_PAGE_DISCLOSURE_DEFAULT_FONT_SIZE,
  STUDIO_DEFAULT_DISCLAIMER_FONT_SIZE,
  STUDIO_DEFAULT_DISCLOSURE_FONT_SIZE,
  STUDIO_DEFAULT_USER_DISCLAIMERS,
  STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MAX,
  STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MIN,
  useModal,
} from 'venn-utils';
import LogoManagementModal from './printing/LogoManagementModal';
import { PrintSetting } from './PrintSetting';
import { cn } from '@/lib/utils';

const PrintSettingsSection = ({ hasDefaultRLAdvisoryFeesFF = false }: { hasDefaultRLAdvisoryFeesFF?: boolean }) => {
  const { hasPermission, profileSettings } = useContext(UserContext);
  const {
    hasDefault,
    defaultLogoId,
    loading,
    needsSaving,
    organizationPrintSettings,
    storeSettings,
    uploadOrganizationLogo,
    setOrganizationName,
    setDisclaimer,
    setCoverPageDisclosure,
    setDisclosure,
    setDisclaimerFontSize,
    setCoverPageDisclosureFontSize,
    setDisclosureFontSize,
    allLogos,
    deleteLogo,
    setDefaultLogo,
  } = useContext(StudioPrintSettingsContext);
  const [isOpen, open, close] = useModal();
  const isReadOnly = !hasPermission('MANAGE_PRINT_CONFIGURATION');
  const organizationDefaultDisclosure = STUDIO_DISCLOSURE_PAGE_DEFAULT_DISCLOSURE(
    organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name,
  );
  const organizationDefaultDisclaimer = STUDIO_DEFAULT_USER_DISCLAIMERS(
    organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name,
  );
  const finalDisclosure = organizationPrintSettings?.disclosure ?? organizationDefaultDisclosure;
  const finalDisclaimer = organizationPrintSettings?.disclaimer ?? organizationDefaultDisclaimer;
  const finalCoverPageDisclosure =
    organizationPrintSettings?.coverPageDisclosure ?? STUDIO_COVER_PAGE_DEFAULT_DISCLOSURE;

  const finalDisclosureFontSize = organizationPrintSettings?.disclosureFontSize ?? STUDIO_DEFAULT_DISCLOSURE_FONT_SIZE;
  const finalDisclaimerFontSize = organizationPrintSettings?.disclaimerFontSize ?? STUDIO_DEFAULT_DISCLAIMER_FONT_SIZE;
  const finalCoverPageDisclosureFontSize =
    organizationPrintSettings?.coverPageDisclosureFontSize ?? STUDIO_COVER_PAGE_DISCLOSURE_DEFAULT_FONT_SIZE;

  return (
    <WrapperForSections>
      <Section>
        <div className="flex justify-between items-center">
          <FlexWrapper>
            <HeaderIcon type="print" />
            <Name>Report Lab Print Configuration</Name>
          </FlexWrapper>
          <div className="flex gap-2">
            <AlertNote size="large">Will update all existing reports</AlertNote>
            <Button
              disabled={!needsSaving || loading || isReadOnly}
              icon="save"
              type="submit"
              onClick={() => {
                organizationPrintSettings && storeSettings(organizationPrintSettings);
              }}
            >
              Save changes
            </Button>
          </div>
        </div>
      </Section>
      <Section>
        <div className="w-full">
          <RequiredLabel className="text-[14px]">Organization Display Name</RequiredLabel>
          <StatefulInput
            value={organizationPrintSettings?.organizationName}
            onChange={(value) => {
              setOrganizationName(value);
            }}
            disabled={loading || isReadOnly}
            placeholder={profileSettings?.organization?.name}
            style={{ display: 'flex' }}
            className="mt-1 [&>input]:focus:border-venn-dark-blue [&>input]:focus:outline-venn-dark-blue"
          />
        </div>
        <div className="mt-5 w-full">
          <RequiredLabel className="text-[14px]">Selected Logo</RequiredLabel>
          <div className="flex gap-[15px] mt-1">
            <div>
              <div className="absolute p-2.5 pl-[100px]">
                <input
                  type="file"
                  id="logoFileDialogId"
                  className="hidden"
                  accept="image/*"
                  onChange={(file) => uploadOrganizationLogo(file.target, true)}
                />
                {!isReadOnly && (
                  <LinkStyle
                    onClick={() => {
                      document.getElementById('logoFileDialogId')?.click();
                    }}
                    className="hover:cursor-pointer print:hidden"
                  >
                    <Tooltip content="Edit Default Logo">
                      <StyledIcon type="edit" className="text-venn-dark-blue" />
                    </Tooltip>
                  </LinkStyle>
                )}
              </div>
              <div className="inline-block">
                <div
                  className={cn([
                    'items-center justify-center h-32 w-32 border border-solid border-[#f00] p-0 m-0 text-center',
                    hasDefault ? 'hidden' : 'flex',
                  ])}
                >
                  Upload or Set Default Logo
                </div>
              </div>
              <img
                src={hasDefault ? `/api/printing/logo/${defaultLogoId}` : ''}
                alt={organizationPrintSettings?.logoId}
                className={cn([
                  'w-32 h-32 object-contain border border-dotted border-[#808080] p-[3px]',
                  hasDefault ? 'inline-block' : 'hidden',
                ])}
              />
            </div>
            <Button onClick={open} aria-label="Manage Logos" refreshedStyling className="w-32 h-32 text-venn-dark-blue">
              Manage Logos
            </Button>
          </div>
        </div>
        {isOpen && (
          <LogoManagementModal
            onClose={close}
            allLogos={allLogos}
            deleteLogo={deleteLogo}
            setDefaultLogo={setDefaultLogo}
            uploadOrganizationLogo={uploadOrganizationLogo}
          />
        )}
      </Section>
      <div className="flex flex-col gap-10 justify-between px-[60px] py-[40px]">
        <PrintSetting
          title="Cover Page Disclosure"
          label="Appears at the bottom of the Cover Page"
          fontTooltip={`Cover Page Disclosure font size can be set to any value between
            ${STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MIN}pts and ${STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MAX}
              pts`}
          settingContent={finalCoverPageDisclosure}
          settingContentOnChange={(content) => setCoverPageDisclosure(content)}
          fontSize={finalCoverPageDisclosureFontSize}
          fontSizeOnChange={(value) => setCoverPageDisclosureFontSize(value)}
          isReadOnly={isReadOnly || loading}
        />
        <PrintSetting
          title="Page Footer Disclaimer"
          label={`Appears on bottom of every page in the report in addition to dynamically-added ${getAppTitle()} disclaimers`}
          fontTooltip={`Disclaimer font size can be set to any value between ${STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MIN}
                      pts and ${STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MAX}
                      pts`}
          settingContent={finalDisclaimer}
          settingContentOnChange={(content) => setDisclaimer(content)}
          fontSize={finalDisclaimerFontSize}
          fontSizeOnChange={(value) => setDisclaimerFontSize(value)}
          isReadOnly={isReadOnly || loading}
        />
        <PrintSetting
          title="Disclosures Page"
          label={`Appears at end of every report in addition to dynamically-added ${getAppTitle()} disclosures`}
          fontTooltip={`Disclosure font size can be set to any value between ${STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MIN}
                      pts and ${STUDIO_DISCLAIMER_DISCLOSURE_FONT_SIZE_MAX}
                      pts`}
          settingContent={finalDisclosure}
          settingContentOnChange={(content) => setDisclosure(content)}
          fontSize={finalDisclosureFontSize}
          fontSizeOnChange={(value) => setDisclosureFontSize(value)}
          isReadOnly={isReadOnly || loading}
        />
      </div>
      {!hasDefaultRLAdvisoryFeesFF && (
        <Section>
          <Label>Investments Excluded from Advisory Fees</Label>
          <Caption className="block mt-2">
            When investments are excluded from portfolio advisory fees, a footnote will be added to each page directing
            readers to the Disclosures page, where all investments excluded from portfolio advisory fees will be
            detailed.
          </Caption>
        </Section>
      )}
    </WrapperForSections>
  );
};

export default PrintSettingsSection;
